import React, { useState, useEffect, useMemo } from 'react'
import AvanceBronce from '../../../../../assets/img/avance_bronce.png'
import AvancePlata from '../../../../../assets/img/avance_plata.png'
import AvanceOro from '../../../../../assets/img/avance_oro.png'
import Perseverancia from '../../../../../assets/img/perseverancia.png'
import Precision from '../../../../../assets/img/precision.png'
import { Trans } from 'react-i18next'
import Estrellas from './Estrellas'

const CONFETTI_COLORS = ['#162e5b', '#18bafb', '#fbbf24', '#34d399', '#f87171', '#a78bfa', '#fb923c', '#ffffff']

const ANIMATIONS = `
  @keyframes confetti-fall {
    0%   { transform: translateY(-10px) rotate(0deg) scale(1); opacity: 1; }
    80%  { opacity: 1; }
    100% { transform: translateY(110vh) rotate(720deg) scale(0.5); opacity: 0; }
  }
  @keyframes bounce-in {
    0%   { transform: scale(0.3) translateY(60px); opacity: 0; }
    55%  { transform: scale(1.06) translateY(-10px); opacity: 1; }
    75%  { transform: scale(0.97) translateY(3px); }
    100% { transform: scale(1) translateY(0); opacity: 1; }
  }
  @keyframes badge-pop {
    0%   { transform: scale(0) rotate(-25deg); opacity: 0; }
    60%  { transform: scale(1.25) rotate(6deg); opacity: 1; }
    100% { transform: scale(1) rotate(0deg); opacity: 1; }
  }

  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50%       { transform: translateY(-7px); }
  }
  @keyframes glow-pulse {
    0%, 100% { box-shadow: 0 4px 24px rgba(24,186,251,0.25); }
    50%       { box-shadow: 0 4px 48px rgba(24,186,251,0.55), 0 0 0 6px rgba(24,186,251,0.08); }
  }
  .pantalla-fin-card { animation: bounce-in 0.75s cubic-bezier(0.34, 1.56, 0.64, 1) both; }
  .badge-pop-anim   { animation: badge-pop 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) both; }
  .float-anim       { animation: float 3.2s ease-in-out infinite; }
  .glow-pulse-anim  { animation: glow-pulse 2.4s ease-in-out infinite; }
`

function Confetti({ visible }) {
  const pieces = useMemo(() =>
    Array.from({ length: 65 }, (_, i) => ({
      id: i,
      color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      left: `${((i * 1.618) % 1) * 100}%`,
      delay: `${((i * 0.37) % 1.8).toFixed(2)}s`,
      duration: `${(2.4 + (i % 7) * 0.25).toFixed(2)}s`,
      size: `${7 + (i % 6)}px`,
      shape: i % 3,
    })), []
  )

  if (!visible) return null

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-10">
      {pieces.map(p => (
        <div
          key={p.id}
          style={{
            position: 'absolute',
            top: '-12px',
            left: p.left,
            width: p.size,
            height: p.size,
            backgroundColor: p.color,
            borderRadius: p.shape === 0 ? '50%' : p.shape === 1 ? '2px' : '0',
            transform: p.shape === 2 ? 'rotate(45deg)' : undefined,
            animation: `confetti-fall ${p.duration} ${p.delay} ease-in forwards`,
            opacity: 0,
          }}
        />
      ))}
    </div>
  )
}

function AnimatedScore({ target }) {
  const [current, setCurrent] = useState(0)

  useEffect(() => {
    if (target === 0) return
    const steps = 35
    const stepValue = target / steps
    let count = 0
    const interval = setInterval(() => {
      count++
      if (count >= steps) {
        setCurrent(target)
        clearInterval(interval)
      } else {
        setCurrent(Math.min(target, stepValue * count))
      }
    }, 38)
    return () => clearInterval(interval)
  }, [target])

  return <>{current.toFixed(2)}</>
}

function PantallaFin({time, calificacion, calificado, intentarDeNuevo, esUMA, isDone, modoPrevisualizar, additionalActData, esEvaluacion, lang}) {
  const [visible, setVisible] = useState(false)
  const [showConfetti, setShowConfetti] = useState(false)
  const [cardReady, setCardReady] = useState(false)

  const score = isDone ? Math.round(isDone.puntuacionUltimoIntento * 100) / 100 : 0
  const tiempo = `${("0" + Math.floor((time / 60000) % 60)).slice(-2)}:${("0" + Math.floor((time / 1000) % 60)).slice(-2)}:${("0" + ((time / 10) % 100)).slice(-2)}`

  useEffect(() => {
    const t1 = setTimeout(() => setVisible(true), 80)
    const t2 = setTimeout(() => {
      if (score >= 10) setShowConfetti(true)
      setCardReady(true)
    }, 350)
    return () => { clearTimeout(t1); clearTimeout(t2) }
  }, [])

  const badges = calificado ? [
    { condition: calificado.insigniaAvanceBronce !== 0,  src: AvanceBronce,  alt: 'Medalla de avance de bronce' },
    { condition: calificado.insigniaAvancePlata !== 0,   src: AvancePlata,   alt: 'Medalla de avance de plata' },
    { condition: calificado.insigniaAvanceOro !== 0,     src: AvanceOro,     alt: 'Medalla de avance de oro' },
    { condition: calificado.insigniaPerseverancia !== 0, src: Perseverancia, alt: 'Medalla de perseverancia' },
    { condition: calificado.insigniaPrecision !== 0,     src: Precision,     alt: 'Medalla de precisión' },
  ].filter(b => b.condition) : []

  return (
    <div className={`relative min-h-[400px] md:min-h-[700px] w-full h-full bg-[#00000087] backdrop-blur-sm flex items-center justify-center transition duration-700 ease-in-out opacity-0 ${visible ? '!opacity-100' : ''}`}>
      <style>{ANIMATIONS}</style>
      <Confetti visible={showConfetti} />

      <div className={`relative z-20 w-[90%] mx-auto max-w-md bg-white p-6 md:p-8 rounded-2xl shadow-2xl text-center my-[7rem] glow-pulse-anim ${cardReady ? 'pantalla-fin-card' : 'opacity-0'}`}>
        <div className="mb-5 text-nimbus text-2xl font-extrabold float-anim">
          <Trans i18nKey='activityLabel'></Trans>
        </div>

        {esEvaluacion ? null :
          <>
            {!modoPrevisualizar && <Estrellas score={score} className='mb-6' />}
            <div className='grid grid-cols-2 gap-3 mb-5'>
              {!modoPrevisualizar &&
                <div className='bg-slate-50 rounded-xl py-3 px-2'>
                  <p className='text-[11px] uppercase tracking-wide text-gray-400 mb-1'><Trans i18nKey='score'></Trans></p>
                  <p className='text-xl font-bold text-nimbus'>
                    {cardReady ? <AnimatedScore target={score} /> : '0.00'}
                  </p>
                </div>
              }
              {!modoPrevisualizar &&
                <div className='bg-slate-50 rounded-xl py-3 px-2'>
                  <p className='text-[11px] uppercase tracking-wide text-gray-400 mb-1'><Trans i18nKey='highestScore'></Trans></p>
                  <p className='text-xl font-bold text-nimbus'>{isDone ? isDone.puntuacionTop : 0}</p>
                </div>
              }
              {!modoPrevisualizar &&
                <div className='bg-slate-50 rounded-xl py-3 px-2'>
                  <p className='text-[11px] uppercase tracking-wide text-gray-400 mb-1'><Trans i18nKey='attemptsNumber'></Trans></p>
                  <p className='text-xl font-bold text-nimbus'>{isDone ? isDone.intento : 0}</p>
                </div>
              }
              <div className='bg-slate-50 rounded-xl py-3 px-2'>
                <p className='text-[11px] uppercase tracking-wide text-gray-400 mb-1'><Trans i18nKey='timeLabel'></Trans></p>
                <p className='text-xl font-bold text-nimbus tabular-nums'>{tiempo}</p>
              </div>
            </div>
            {additionalActData && additionalActData.map((el) => (
              <div key={el.type} className='text-sm text-gray-600 mb-2'>
                <Trans>{el.type}</Trans>: {el.value} <Trans>{el.label}</Trans>
              </div>
            ))}
          </>
        }

        {esUMA && <div className='grid gap-y-3 mt-1 mb-1 border-t border-gray-100 pt-5'>
          <h1 className='text-nimbus text-lg font-semibold'><Trans i18nKey="insignsLabel"></Trans></h1>
          <div className='flex gap-x-3 justify-center flex-wrap'>
            {badges.map((badge, idx) => (
              <img
                key={badge.alt}
                src={badge.src}
                alt={badge.alt}
                className='w-16 h-16 object-contain badge-pop-anim'
                style={{ animationDelay: `${0.9 + idx * 0.18}s`, opacity: 0 }}
              />
            ))}
          </div>
        </div>}

        <button
          className="bg-nimbus hover:opacity-90 text-white mt-6 py-3 px-6 rounded-full flex items-center justify-center w-full font-semibold shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all duration-150"
          onClick={intentarDeNuevo}
        >
          <Trans i18nKey='tryAgainLabel'></Trans>
        </button>
      </div>
    </div>
  )
}

export default PantallaFin
